package dev.lingvo.site.components.marketing

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.letterSpacing
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.displayFont
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.ds.onInkPalette
import dev.lingvo.site.ds.toSitePalette
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * The landing page is a stack of full-bleed bands, and this is the only thing that sets their edges.
 *
 * Every band shares one gutter ([Space.Gutter]) and one vertical rhythm, so the page reads as one
 * sheet rather than as sections that were each laid out on their own day. The ground alternates
 * between three values and nothing else — paper, its one step down, and ink — because a page whose
 * bands each pick their own tint stops having a spine.
 */
@Composable
fun Band(
    ground: BandGround = BandGround.Paper,
    labelledBy: String? = null,
    content: @Composable () -> Unit,
) {
    Section(
        BandStyle.toModifier()
            .then(
                when (ground) {
                    BandGround.Paper -> BandPaperStyle.toModifier()
                    BandGround.PaperSunken -> BandPaperSunkenStyle.toModifier()
                    BandGround.Ink -> BandInkStyle.toModifier()
                },
            )
            .toAttrs { labelledBy?.let { attr("aria-labelledby", it) } },
    ) {
        Div(BandInnerStyle.toModifier().toAttrs()) { content() }
    }
}

/**
 * Three grounds, deliberately.
 *
 * [Ink] is the emphatic one and is spent twice on the whole page — the arithmetic band and the
 * close. A third would make it the page's normal state and leave nothing to emphasise with.
 */
enum class BandGround { Paper, PaperSunken, Ink }

/**
 * The label above a band's heading: small, mono, tracked, and in the accent.
 *
 * It exists to say what kind of claim is coming, so the heading itself does not have to spend words
 * on framing. On ink it takes the on-dark accent, which is a different colour rather than the same
 * one at a different opacity — an accent thinned against ink fails contrast long before it looks
 * wrong.
 */
@Composable
fun BandLabel(text: String, onInk: Boolean = false) {
    Div(
        BandLabelStyle.toModifier()
            .then(if (onInk) BandLabelInkStyle.toModifier() else Modifier)
            .toAttrs(),
    ) { Span { Text(text) } }
}

/** A band's heading. One per band; the page has no h3-level structure and does not need one. */
@Composable
fun BandHeading(text: String, id: String? = null, onInk: Boolean = false, measure: Int = 640) {
    H2(
        BandHeadingStyle.toModifier()
            .maxWidth(measure.px)
            .then(if (onInk) BandHeadingInkStyle.toModifier() else Modifier)
            .toAttrs { id?.let { attr("id", it) } },
    ) { Text(text) }
}

val BandStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .padding(topBottom = Space.Gutter, leftRight = Space.Gutter)
    }
    // The gutter is a desktop measure. Below that it would eat the content it exists to frame.
    Breakpoint.ZERO { Modifier.padding(topBottom = Space.Section, leftRight = Space.XL) }
    Breakpoint.MD { Modifier.padding(topBottom = Space.Hero, leftRight = Space.Hero) }
    Breakpoint.LG { Modifier.padding(topBottom = Space.Gutter, leftRight = Space.Gutter) }
}

/**
 * Every band stacks its own content at one rhythm, so no band has to decide its internal gaps — and
 * every band's content is capped at the same measure and centred.
 *
 * The band itself stays full-bleed, because the alternating grounds are the page's spine and a
 * centred ink band with paper either side would read as a card. Only what sits *inside* it is
 * bounded. Without this the gutter was the only constraint, so on a wide monitor the bands ran to
 * the window while the header held a column — which is exactly what it looked like.
 */
val BandInnerStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .maxWidth(Space.ContentMax)
        .styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
            property("gap", Space.XXXL.toString())
            property("margin-inline", "auto")
        }
}

val BandPaperStyle = CssStyle.base {
    Modifier.styleModifier { property("background-color", colorMode.toSitePalette().surfaceBase.toString()) }
}

val BandPaperSunkenStyle = CssStyle.base {
    Modifier.styleModifier { property("background-color", colorMode.toSitePalette().surfaceSunken.toString()) }
}

val BandInkStyle = CssStyle.base {
    Modifier
        .styleModifier { property("background-color", colorMode.toSitePalette().textPrimary.toString()) }
        .color(colorMode.toSitePalette().surfaceBase)
}

val BandLabelStyle = CssStyle.base {
    Modifier
        .monoFont()
        .fontSize(TypeScale.Micro)
        .letterSpacing(TypeScale.MicroTracking)
        .color(colorMode.toSitePalette().accent)
        .styleModifier { property("text-transform", "uppercase") }
}

/**
 * The accent, on ink.
 *
 * Not the same colour at a lower opacity — a different one. Measured against the ink ground
 * (`#14120F`), the light palette's accent `#4436F0` lands at **2.70:1**, which fails the 4.5:1 text
 * floor and the 3:1 non-text floor alike. The shared `onInkPalette` helper selects the accent
 * measured for whichever ground the current mode paints.
 */
val BandLabelInkStyle = CssStyle.base {
    Modifier.color(colorMode.onInkPalette().accent)
}

val BandHeadingStyle = CssStyle {
    base {
        Modifier
            .displayFont()
            .fontSize(TypeScale.Display)
            .fontWeight(700)
            .lineHeight(TypeScale.DisplayLineHeight)
            .letterSpacing((-1.5).px)
            .margin(0.px)
            .color(colorMode.toSitePalette().textPrimary)
    }
    Breakpoint.ZERO { Modifier.fontSize(TypeScale.Heading) }
    Breakpoint.MD { Modifier.fontSize(TypeScale.Display) }
}

val BandHeadingInkStyle = CssStyle.base {
    Modifier.color(colorMode.toSitePalette().surfaceBase)
}
